// FAIR (Factor Analysis of Information Risk) quantitative assessment types.
// Ported from the standalone FAIR Assessor app's data model so a risk's optional
// FAIR analysis follows the same Open FAIR ontology and calculation engine.

export type RatingLevel = 'VL' | 'L' | 'M' | 'H' | 'VH';

export type AssessmentStatus = 'draft' | 'in-progress' | 'complete';

/** Concrete events-per-year anchor for CF calibration (overrides the qualitative CF band in derived estimates). */
export type CfAnchor = '1-2' | '5-10' | '12' | '52' | '250';

export interface QualitativeInputs {
  // Threat Event Frequency
  contactFrequency: RatingLevel;
  probabilityOfAction: RatingLevel;
  // Vulnerability
  threatCapability: RatingLevel;
  controlStrength: RatingLevel;
  // Loss Magnitude - Primary
  productivity: RatingLevel;
  response: RatingLevel;
  replacement: RatingLevel;
  competitiveAdvantage: RatingLevel;
  finesJudgements: RatingLevel;
  reputation: RatingLevel;
  // Loss Magnitude - Secondary
  secondaryLef: RatingLevel;          // Probability secondary stakeholders take action
  secondaryLegal: RatingLevel;
  secondaryRegulatory: RatingLevel;
}

export interface QuantitativeRange {
  min: number;
  mostLikely: number;
  max: number;
}

export interface QuantitativeInputs {
  contactFrequencyPerYear: QuantitativeRange;
  probabilityOfAction: QuantitativeRange;       // 0-1
  threatCapability: QuantitativeRange;          // 0-1
  controlStrength: QuantitativeRange;           // 0-1
  primaryLossMagnitude: QuantitativeRange;      // currency
  secondaryLossMagnitude: QuantitativeRange;    // currency
  rationale?: string;
}

export interface MonteCarloResult {
  iterations: number;
  annualLossExposure: { p10: number; p50: number; p90: number; mean: number };
  lossEventFrequency: { p10: number; p50: number; p90: number; mean: number };
  histogram: number[];   // 20 buckets for the ALE distribution chart
}

export interface FairResults {
  qualitativeScore: number;        // 0-100
  qualitativeLabel: 'Very Low' | 'Low' | 'Medium' | 'High' | 'Very High';
  riskColor: 'success' | 'info' | 'warning' | 'danger' | 'dark';
  tef: RatingLevel;                // Threat Event Frequency
  vulnerability: RatingLevel;
  lef: RatingLevel;                // Loss Event Frequency
  primaryLoss: RatingLevel;
  secondaryLoss: RatingLevel;
  totalLoss: RatingLevel;
  monteCarlo?: MonteCarloResult;
}

export interface FairAssessment {
  enabled: boolean;
  assetId: string;
  threatId: string;
  status: AssessmentStatus;
  createdAt: string;
  updatedAt: string;
  qualitative: QualitativeInputs;
  quantitativeEnabled: boolean;
  quantitative?: QuantitativeInputs;
  results?: FairResults;
  cfAnchor?: CfAnchor;
  /** Set only for assessments backfilled from a legacy import that never rated
   * individual factors - the ratings below are neutral defaults, not real input. */
  importNote?: string;
}

export const EMPTY_QUALITATIVE: QualitativeInputs = {
  contactFrequency: 'M',
  probabilityOfAction: 'M',
  threatCapability: 'M',
  controlStrength: 'M',
  productivity: 'M',
  response: 'M',
  replacement: 'M',
  competitiveAdvantage: 'M',
  finesJudgements: 'M',
  reputation: 'M',
  secondaryLef: 'M',
  secondaryLegal: 'M',
  secondaryRegulatory: 'M',
};

export const EMPTY_FAIR_ASSESSMENT: FairAssessment = {
  enabled: false,
  assetId: '',
  threatId: '',
  status: 'draft',
  createdAt: '',
  updatedAt: '',
  qualitative: EMPTY_QUALITATIVE,
  quantitativeEnabled: false,
};
